// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    order: state.pages.orderStatus.order,
    showPoNumber: getSettingsCollection(state).orderSettings.showPoNumber,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OrderStatusInformationStyles {
    informationWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    orderNumberText?: TypographyPresentationProps;
    statusText?: TypographyPresentationProps;
    orderDateText?: TypographyPresentationProps;
    termsText?: TypographyPresentationProps;
    poNumberText?: TypographyPresentationProps;
    shippingMethodText?: TypographyProps;
    vatNumberWrapper?: InjectableCss;
    vatNumberText?: TypographyPresentationProps;
    // XNG-Changes
    shippingAddressWrapper?: InjectableCss;
    billingAddressWrapper?: InjectableCss;
    leftColumn?: InjectableCss;
    rightColumn?: InjectableCss;
    addressDisplay?: AddressInfoDisplayStyles;
    addressText?: InjectableCss;
    shippingMethodeWrapper?: InjectableCss;
    shippinMethodTitle?: TypographyPresentationProps;
}

export const orderStatusInformationStyles: OrderStatusInformationStyles = {
    informationWrapper: {
        css: css`
            display: flex;
            flex-direction: row;

            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-flow: wrap;
                    `,
                    css`
                        flex-flow: wrap;
                    `,
                    css`
                        flex-flow: wrap;
                    `,
                    css`
                        flex-flow: wrap;
                    `,
                    null,
                ])}
        `,
    },
    wrapper: {
        css: css`
            margin-right: 25px;

            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 50%;
                        margin-right: 0;
                    `,
                    css`
                        width: 50%;
                        margin-right: 0;
                    `,
                    css`
                        width: 33.3%;
                        margin-right: 0;
                    `,
                    css`
                        width: 25%;
                        margin-right: 0;
                    `,
                    null,
                ])}
        `,
    },
    titleText: {
        variant: "h6",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 5px;
        `,
    },
    vatNumberWrapper: {
        css: css`
            margin-top: 10px;
        `,
    },
};

const styles = orderStatusInformationStyles;

const OrderStatusInformation = ({ order, showPoNumber, enableVat }: Props) => {
    if (!order) {
        return null;
    }

    // XNG-Changes
    const hasShippingAddress = order.fulfillmentMethod === FulfillmentMethod.Ship || !order.fulfillmentMethod;

    const hasShippingMethod =
        (order.fulfillmentMethod === FulfillmentMethod.Ship || !order.fulfillmentMethod) && order.shipCode;

    // XNG-Changes: restructuring to match order details styling
    return (
        <StyledWrapper {...styles.informationWrapper} data-test-selector="orderStatusInformation">
            <StyledWrapper {...styles.leftColumn}>
                {order.orderDate && (
                    <StyledWrapper {...styles.wrapper}>
                        <Typography as="h2" {...styles.titleText} id="orderStatusOrderDate">
                            {translate("Order Date")}
                        </Typography>
                        <Typography {...styles.orderDateText} aria-labelledby="orderStatusOrderDate">
                            <LocalizedDateTime
                                dateTime={order.orderDate}
                                options={{ year: "numeric", month: "numeric", day: "numeric" }}
                            />
                        </Typography>
                    </StyledWrapper>
                )}
                {/* <StyledWrapper {...styles.wrapper}>
                        <Typography as="h2" {...styles.titleText} id="orderStatusOrderNumber">
                            {translate("Order Number")}
                        </Typography>
                        <Typography {...styles.orderNumberText} aria-labelledby="orderStatusOrderNumber">
                            {order.webOrderNumber || order.erpOrderNumber}
                        </Typography>
                    </StyledWrapper> */}
                {showPoNumber && order.customerPO && (
                    <StyledWrapper {...styles.wrapper}>
                        <Typography as="h2" {...styles.titleText} id="orderStatusPO">
                            {translate("PO")}
                        </Typography>
                        <Typography {...styles.poNumberText} aria-labelledby="orderStatusPO">
                            {order.customerPO}
                        </Typography>
                    </StyledWrapper>
                )}
                {order.statusDisplay && (
                    <StyledWrapper {...styles.wrapper}>
                        <Typography as="h2" {...styles.titleText} id="orderStatusStatus">
                            {translate("Status")}
                        </Typography>
                        <Typography {...styles.statusText} aria-labelledby="orderStatusStatus">
                            {order.status}
                        </Typography>
                    </StyledWrapper>
                )}
                {enableVat && order.customerVatNumber && (
                    <StyledWrapper {...styles.vatNumberWrapper}>
                        <Typography as="h2" {...styles.titleText} id="orderStatusVatNumber">
                            {translate("VAT Number")}
                        </Typography>
                        <Typography {...styles.vatNumberText} aria-labelledby="orderStatusVatNumber">
                            {order.customerVatNumber}
                        </Typography>
                    </StyledWrapper>
                )}
                {order.terms && (
                    <StyledWrapper {...styles.wrapper}>
                        <Typography as="h2" {...styles.titleText} id="orderStatusTerms">
                            {translate("Terms")}
                        </Typography>
                        <Typography {...styles.termsText} aria-labelledby="orderStatusTerms">
                            {order.terms}
                        </Typography>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.shippingAddressWrapper}>
                    <Typography as="h2" {...styles.addressText}>
                        {translate(hasShippingAddress ? "Shipping Address" : "Pick Up Location")}
                    </Typography>
                    <AddressInfoDisplay
                        companyName={order.stCompanyName}
                        address1={order.stAddress1}
                        address2={order.stAddress2}
                        city={order.shipToCity}
                        state={order.shipToState}
                        postalCode={order.shipToPostalCode}
                        country={order.stCountry}
                        extendedStyles={styles.addressDisplay}
                    />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.rightColumn}>
                <StyledWrapper {...styles.billingAddressWrapper}>
                    <Typography as="h2" {...styles.addressText}>
                        {translate("Billing Address")}
                    </Typography>
                    <AddressInfoDisplay
                        companyName={order.btCompanyName}
                        address1={order.btAddress1}
                        address2={order.btAddress2}
                        city={order.billToCity}
                        state={order.billToState}
                        postalCode={order.billToPostalCode}
                        country={order.btCountry}
                        extendedStyles={styles.addressDisplay}
                    />
                </StyledWrapper>
                {hasShippingMethod && (
                    <StyledWrapper {...styles.shippingMethodeWrapper}>
                        <Typography as="h2" {...styles.shippinMethodTitle} id="orderStatusShippingMethod">
                            {translate("Shipping Method")}
                        </Typography>
                        <Typography {...styles.shippingMethodText} aria-labelledby="orderStatusShippingMethod">
                            {order.shipViaDescription || order.shipCode}
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OrderStatusInformation),
    definition: {
        group: "Order Status",
        displayName: "Information",
        allowedContexts: ["OrderStatusPage"],
    },
};

export default widgetModule;
